import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, useToast } from '@chakra-ui/react';
import { getSupabase, supabasePublic, supabaseSchema } from '../components/supabaseClient';
import { bucketName_files, tableName, colid, prompts, getTypeFieldIndex } from "../components/env";
import { generateMedADocsFolderPath } from "./documentUtilities";
import { dbgLog } from "../utils/appDebugFlags";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import {
  hydrateScheduleRowsTimezoneSource,
  fetchViewerIanaTimezone,
} from "../utils/scheduleRowTimezone.js";

const cLog = (...args) => dbgLog("combine-document", ...args);

// Add these column name constants
const coltime = 'time_';
const colid2 = 'patient_name';

export default function CombineDocument({ date_, id }) {
  // Fallback to today's date if date_ is missing
  const effectiveDate = (date_ && date_.trim() !== '') ? date_ : new Date().toISOString().split('T')[0];
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const supabase = await getSupabase();
      setViewerTimezone(await fetchViewerIanaTimezone(supabase));
    })();
  }, []);

  useEffect(() => {
    fetchData();
  }, [effectiveDate]);

  const fetchData = async () => {
    if (!effectiveDate || effectiveDate.trim() === '') {
      console.error("effectiveDate is undefined or empty in fetchData");
      return;
    }
    
    const supabase = await getSupabase();
    cLog("🔍 FETCH DATA STARTING");
    try {
      const { data: patientData, error } = await supabase
        .from(tableName)
        .select("*")
        .eq("date_", effectiveDate);

      if (error) {
        console.error("Error fetching data:", error);
      } else {
        const hydrated = await hydrateScheduleRowsTimezoneSource(
          supabase,
          patientData || [],
        );
        setData(hydrated);
      }
    } catch (err) {
      console.error("Unexpected error fetching data:", err);
    }
  };

  // Format the date (MM/DD/YY)
  if (!effectiveDate || effectiveDate.trim() === '') {
    console.error("effectiveDate is undefined or empty in CombineDocument component");
    return null; // Return null to prevent rendering
  }
  
  const [year, month, day] = effectiveDate.split('-');
  const displayDate = `${month}/${day}/${year.slice(2)} Dr Pallickal`;

  // Convert time string to minutes since midnight for comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const time = timeStr.split(':').slice(0, 2);
    const hours = parseInt(time[0]);
    const minutes = parseInt(time[1]);
    return hours * 60 + minutes;
  };

  // Combine documents logic
  const handleCombineDocuments = async () => {
    if (!effectiveDate || effectiveDate.trim() === '') {
      console.error("effectiveDate is undefined or empty in handleCombineDocuments");
      toast({
        title: "Error",
        description: "Date information is missing. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    const supabase = await getSupabase();
    cLog("📚 COMBINE DOCUMENTS STARTING");
    setIsLoading(true);
    try {
      const year = effectiveDate.split('-')[0];
      
      // Use the already-parsed active schema from supabaseClient (single source of truth)
      const userSchema = supabaseSchema || "public";
      
      // Use helper function to generate folder path with schema
      const folderPath = generateMedADocsFolderPath(year, effectiveDate, userSchema);
      cLog("📂 Processing folder:", folderPath);
      cLog("📅 Using date:", effectiveDate);

      const { data: files, error: listError } = await supabase.storage
        .from(bucketName_files)
        .list(folderPath);

      cLog("📄 Files found:", files);

      if (listError) throw listError;

      cLog("\n=== CombineDocument Debug ===");
      cLog("1. Files from storage:", files);
      
      const jsonFiles = files.filter(file => 
        file.name.endsWith('.json') && !file.name.includes('combined')
      );
      cLog("2. Filtered JSON files:", jsonFiles);
      
      cLog("3. Patient data array:", data);

      // Map filenames to patient data
      const filesWithData = jsonFiles.map(file => {
        // Extract EHR_ID from filename (assuming format: "Name-EHRID.json")
        const ehrId = file.name.split('-').pop().replace('.json', '');
        // Find matching patient data
        const patientData = data.find(p => p.EHR_ID === ehrId);
        return {
          ...file,
          time: patientData ? patientData[coltime] : null,
          patientName: patientData ? patientData[colid2] : null
        };
      });

      // Sort by time
      filesWithData.sort((a, b) => {
        if (!a.time) return 1;  // Push null times to end
        if (!b.time) return -1;
        const timeA = timeToMinutes(a.time);
        const timeB = timeToMinutes(b.time);
        return timeA - timeB;
      });

      cLog("5. Files after sorting:");
      filesWithData.forEach((f) => cLog(`   ${f.name} - Time: ${f.time}`));

      let finalDelta = { ops: [] };
      let combinedContent = '\n\n';  // Reduced spacing at top

      // Add initial spacing without the heading
      finalDelta.ops.push({
        insert: '\n\n'
      });

      cLog('Files to combine:', filesWithData);

      for (const file of filesWithData) {
        cLog('Processing file:', file.name);
        
        const { data: fileData, error } = await supabase.storage
          .from(bucketName_files)
          .download(`${folderPath}/${file.name}`);

        if (error) {
          console.error('Error downloading file:', file.name, error);
          continue;
        }

        const patientId = file.name.split('-')[0].replace('.json', '');
        const patientData = data.find(p => p[prompts[0][0]] === patientId);
        
        if (!patientData) {
          console.error('No patient data found for:', patientId);
          continue;
        }

        const time = convertScheduledTime(
          patientData.date_,
          patientData.time_,
          patientData.timezone_source ?? null,
          viewerTimezone,
        );
        
        // Handle different table configurations for header generation
        let headerText;
        if (tableName === "notes-c") {
          // For notes-c table, use EHR_ID instead of email
          const ehrId = patientData.EHR_ID || patientData[colid] || "";
          const typeIndex = getTypeFieldIndex();
          const type = patientData[prompts[typeIndex][0]] || ""; // Use type field instead of format
          headerText = `${time} ${patientData[prompts[0][0]]} - ${ehrId} ${type}\n`;
          
          cLog("🔧 CombineDocument - notes-c table header generation:", {
            time,
            patientName: patientData[prompts[0][0]],
            ehrId,
            type,
            headerText: headerText.trim()
          });
        } else {
          // For other tables, use email instead of EHR_ID
          const typeIndex = getTypeFieldIndex();
          const type = patientData[prompts[typeIndex][0]] || ""; // Use type field instead of format
          headerText = `${time} ${patientData[prompts[0][0]]} - ${patientData[colid]} ${type}\n`;
          
          cLog("🔧 CombineDocument - other table header generation:", {
            time,
            patientName: patientData[prompts[0][0]],
            colid: patientData[colid],
            type,
            headerText: headerText.trim()
          });
        }
        
        // Add header
        finalDelta.ops.push({
          insert: headerText,
          attributes: { header: 1 }  // Changed to header level 1
        });

        // Parse and add content
        const fileContent = await fileData.text();
        const parsedContent = JSON.parse(fileContent);

        if (parsedContent.delta && parsedContent.delta.ops) {
          // Skip the first header in the content
          const contentOps = parsedContent.delta.ops.slice(1);
          finalDelta.ops.push(...contentOps);
          
          // Add separator
          finalDelta.ops.push({ 
            insert: '\n---\n\n'
          });
        }

        combinedContent += `${headerText}${parsedContent.content}\n---\n\n`;
      }

      // Get the first patient's information for the combined document title
      const firstPatient = data[0];
      let combinedTitle;
      
      if (firstPatient && tableName === "notes-c") {
        // For notes-c table, use the format: 9:30 Last, First - 123456 FFP
        const time = firstPatient.time_
          ? convertScheduledTime(
              firstPatient.date_,
              firstPatient.time_,
              firstPatient.timezone_source ?? null,
              viewerTimezone,
            )
          : "00:00";
        const patientName = firstPatient[prompts[0][0]] || "";
        const ehrId = firstPatient.EHR_ID || "";
        const typeIndex = getTypeFieldIndex();
        const type = firstPatient[prompts[typeIndex][0]] || ""; // type field
        combinedTitle = `${time} ${patientName} - ${ehrId} ${type}`;
      } else if (firstPatient) {
        // For other tables, use email instead of EHR_ID
        const time = firstPatient.time_
          ? convertScheduledTime(
              firstPatient.date_,
              firstPatient.time_,
              firstPatient.timezone_source ?? null,
              viewerTimezone,
            )
          : "00:00";
        const patientName = firstPatient[prompts[0][0]] || "";
        const email = firstPatient[colid] || "";
        const typeIndex = getTypeFieldIndex();
        const type = firstPatient[prompts[typeIndex][0]] || ""; // type field
        combinedTitle = `${time} ${patientName} - ${email} ${type}`;
      } else {
        combinedTitle = displayDate;
      }

      cLog("🔧 CombineDocument - Combined title:", combinedTitle);

      const combinedDocument = {
        title: combinedTitle,
        created_at: new Date().toISOString(),
        content: combinedContent,
        delta: finalDelta,
        sections: [],
        lastUpdated: new Date().toISOString()
      };

      const combinedFileName = `${effectiveDate}-combined.json`;
      const combinedFilePath = `${folderPath}/${combinedFileName}`;

      const blob = new Blob([JSON.stringify(combinedDocument, null, 2)], {
        type: 'application/json'
      });

      const { error: uploadError } = await supabase.storage
        .from(bucketName_files)
        .upload(combinedFilePath, blob, {
          upsert: true,
          contentType: 'application/json'
        });

      if (uploadError) throw uploadError;

      navigate(`/patient/combined/edit-document`, {
        state: {
          name: combinedTitle,
          date_: effectiveDate,
          id: 'combined',
          isCombined: true,
          type: firstPatient ? firstPatient[prompts[getTypeFieldIndex()][0]] || '' : ''
        }
      });

      setTimeout(() => {
        window.location.reload();
      }, 100);

      toast({
        title: "Documents Combined Successfully",
        description: "Opening combined document view...",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error combining documents:', error);
      toast({
        title: "Error Combining Documents",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      colorScheme="green"
      onClick={handleCombineDocuments}
      isLoading={isLoading}
      loadingText="Combining"
      flex="0 1 auto"
      minW="140px"
    >
      Combine Documents
    </Button>
  );
}
